import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "react-responsive";
import { tokens, Typography } from "@sunrun/experience-ui-components";
import { isSunrunManaged } from "@sunrun/sales-experience-shared";
import styled from "@emotion/styled/macro";
import type { UserProfile } from "../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { AgendaStep, SolarStepsContent } from "components/molecules/SolarSteps";
import { OPTY_PROGRESS_STEPS } from "checkout/util/constants";
import { MobileAppDownloadBanner } from "cap/components/molecules/MobileAppDownloadBanner";
import { FormReferral } from "components/organisms/Referral";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import heroImage from "components/assets/images/heroImage.png";
import { useAuthToken } from "hooks/useAuthToken";
import { useAppSelector } from "store";
import { useContactsData } from "utils/swrHooks";
import { getPrimaryContact } from "utils/contacts";

type CheckoutFinalizeSplashModalProps = {
  open: boolean;
  prospectId: string;
  userProfile?: UserProfile;
  isNewHomes?: boolean;
  onClose: () => void;
};

const CheckoutFinalizeSplashModal = ({
  open,
  prospectId,
  userProfile,
  isNewHomes,
  onClose,
}: CheckoutFinalizeSplashModalProps) => {
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const hybridToken = useAppSelector((state) => state.auth.hybridToken) ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = Boolean(proposalToken);
  const { contacts } = useContactsData(prospectId, hybridToken, proposalToken);
  const primaryContact = getPrimaryContact(contacts);
  const firstName = primaryContact?.customerFirstName;
  const showReferral =
    !isCustomer && Boolean(userProfile && isSunrunManaged(userProfile));

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <Overlay data-testid="checkout-finalize-splash-modal" onClick={onClose}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-label="Checkout complete"
        onClick={(event) => event.stopPropagation()}
      >
        <ScrollArea>
          <Hero $background={heroImage} $isMobile={isMobile}>
            <HeroCopy>
              <Typography color={tokens.WHITE} size={32} fontWeight="medium">
                {firstName
                  ? `Congratulations, ${firstName}!`
                  : "Congratulations!"}
              </Typography>
              <Typography color={tokens.WHITE} size={20}>
                You’re one step closer to your energy system
              </Typography>
            </HeroCopy>
            <CloseButton
              type="button"
              aria-label="Close"
              data-testid="checkout-finalize-splash-close"
              onClick={onClose}
            >
              ×
            </CloseButton>
          </Hero>
          <NextSteps $isMobile={isMobile}>
            <SolarStepsContent
              showSolarStepsDetails
              showDoubleColumnStyles={isTablet}
              showSingleColumnStyles={isMobile}
            >
              {OPTY_PROGRESS_STEPS.map((solarStep) => (
                <AgendaStep
                  key={solarStep.stepTitle}
                  showSingleColumnStyles={isMobile}
                  showDoubleColumnStyles={isTablet}
                  img={solarStep.img}
                  stepNumber={solarStep.stepTitle}
                  description={solarStep.description}
                  color={tokens.BRAND_MEDIUM_BLUE}
                  completed={solarStep.stepTitle === "Step 1"}
                  current={solarStep.stepTitle === "Step 2"}
                />
              ))}
            </SolarStepsContent>
            {showReferral && (
              <ReferralFormContainer data-testid="checkout-finalize-splash-referral">
                <FormReferral contactId={primaryContact?.contactId} />
              </ReferralFormContainer>
            )}
          </NextSteps>
          <MobileAppDownloadBanner
            pageContext="Offer Review"
            layout="new"
            isNewHomes={isNewHomes}
            isCustomer={isCustomer}
            clipPhone
          />
        </ScrollArea>
      </Dialog>
    </Overlay>,
    document.body
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Dialog = styled.div`
  display: flex;
  width: min(980px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  background: ${tokens.WHITE};
  border-radius: 16px;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(31, 38, 71, 0.25) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(31, 38, 71, 0.25);
    border-radius: 999px;
  }
`;

const Hero = styled.div<{ $background: string; $isMobile: boolean }>`
  min-height: 126px;
  padding: ${({ $isMobile }) => ($isMobile ? "24px" : "24px 48px")};
  display: flex;
  align-items: center;
  gap: 24px;
  background-image: linear-gradient(
      180deg,
      rgba(20, 28, 42, 0.15) 0%,
      rgba(31, 38, 71, 0.55) 100%
    ),
    url(${({ $background }) => $background});
  background-size: cover;
  background-position: center;
`;

const HeroCopy = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: ${tokens.WHITE};
  color: ${tokens.BRAND_HERO_BLUE};
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
`;

const NextSteps = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: ${({ $isMobile }) => ($isMobile ? "24px" : "24px 48px 32px")};
  background: ${tokens.CHARGEBLUE_70};
`;

const ReferralFormContainer = styled.div`
  width: 100%;
`;

export { CheckoutFinalizeSplashModal };
export type { CheckoutFinalizeSplashModalProps };
